'use client';

import { trackedEntity } from '@cineamo/pulse';
import { usePulseTracker } from '@cineamo/pulse/react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { NoOnlineTicketUrlModal } from '@/components/NoOnlineTicketUrlModal';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { Client } from '@cineamo/api/constants/Client.enum';
import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { getOnlineTicketingLinkFlag } from '@/helpers/feature-flags';
import { handoffHref, isHandoffTarget } from '@/helpers/pulse-handoff';
import { appendReservationShowingId } from '@/helpers/reservation-ticket-url-helper';
import { onTicketButtonClick } from '@/helpers/ticket-purchase-intent-helper';
import { isDatetimePast } from '@/helpers/time-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { getHoursUntilShowing, getOnlineTicketUrl } from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { usePageName } from '@/utils/tracking-helper';

/** `movieId` is the film behind the showing's content, so a handoff click is attributed like the browser one. */
export function useTicketUrl(showing: ShowingDto, movieId?: number | null) {
    const pageName = usePageName();
    const searchParams = useSearchParams();
    const domainConfiguration = useDomainConfiguration();
    const [useNativeOnlineTicketingFlag] = useState<boolean>(() => getOnlineTicketingLinkFlag(searchParams.toString()));

    const resolvedTicketUrl = useNativeOnlineTicketingFlag
        ? `/booking/${showing.id}`
        : getOnlineTicketUrl(showing, Client.CinemaWebsite);
    // Preselect the showing when the ticket link points to this cinema's own /reservation page.
    const rawTicketUrl = appendReservationShowingId(
        resolvedTicketUrl ?? undefined,
        showing.id,
        domainConfiguration.domain
    );
    // On a handoff cinema the route tracks `ticket_click` server-side, so the browser sends nothing.
    const isHandoff = Boolean(
        domainConfiguration.websiteConfiguration?.pulseHandoff && rawTicketUrl && isHandoffTarget(rawTicketUrl)
    );
    const ticketUrl =
        isHandoff && rawTicketUrl
            ? handoffHref(rawTicketUrl, {
                  showingId: showing.id,
                  movieId,
                  contentId: showing.contentId,
                  showingTagIds: showing.showingTagIds,
                  page: pageName
              })
            : rawTicketUrl;
    const hasTicketUrl = (ticketUrl?.length ?? 0) > 0;
    // The handoff href is internal but replaces an external link, so it keeps opening in a new tab.
    const isExternal = hasTicketUrl && (isHandoff || !ticketUrl?.startsWith('/'));
    const target = isExternal ? '_blank' : '_self';

    return { ticketUrl, hasTicketUrl, isExternal, target, isHandoff };
}

type TicketLinkProps = Readonly<{
    showing: ShowingDto;
    content?: CachedContentData;
    children: React.ReactNode;
    className?: string;
    onClick?: (e: React.MouseEvent) => void;
}>;

export default function TicketLink({
    showing,
    content,
    children,
    className,
    onClick
}: TicketLinkProps): React.JSX.Element {
    const t = useTranslations();
    const { track: pulseTrack } = usePulseTracker();
    const { track } = useAnalytics();
    const currentPageName = usePageName();

    const { ticketUrl, hasTicketUrl, isExternal, isHandoff } = useTicketUrl(showing, content?.movieId);
    const isDisabled = isDatetimePast(showing.startDatetime, true);
    const [noTicketUrlIsOpen, setNoTicketUrlIsOpen] = useState(false);

    function handleClick(e: React.MouseEvent) {
        onClick?.(e);
        if (e.defaultPrevented) return;

        if (!isHandoff) {
            pulseTrack({
                ...trackedEntity({ movieId: content?.movieId, contentId: showing.contentId }),
                event_type: 'ticket_click',
                context: {
                    showing_id: showing.id,
                    ...(showing.showingTagIds?.length && { showing_tag_ids: showing.showingTagIds }),
                    page: currentPageName
                }
            });
        }

        if (!hasTicketUrl) {
            setNoTicketUrlIsOpen(true);
            return;
        }

        if (isDisabled) {
            return;
        }

        onTicketButtonClick(showing.id, PurchaseReasonEnum.CINEMA_WEBSITE, showing.contentId);
        track('Ticket Button Clicked', {
            page: currentPageName,
            showingId: showing.id,
            showingName: showing.name || content?.title || 'N/A',
            contentId: showing.contentId,
            contentName: content?.name || 'N/A',
            hoursUntilShowing: getHoursUntilShowing(showing.startDatetime) || 'N/A'
        });
    }

    return (
        <>
            {hasTicketUrl && !isDisabled ? (
                <a
                    href={ticketUrl}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'nofollow noopener noreferrer' : undefined}
                    className={className}
                    onClick={handleClick}>
                    <span className="sr-only">{t('tickets.tickets')} </span>
                    {children}
                    {isExternal && <span className="sr-only"> {t('program.ticket-external-hint')}</span>}
                </a>
            ) : (
                <button
                    onClick={handleClick}
                    className={cn(
                        'appearance-none border-0 bg-transparent p-0 m-0 text-left text-inherit w-full',
                        className
                    )}>
                    <span className="sr-only">{t('tickets.tickets')} </span>
                    {children}
                </button>
            )}
            {noTicketUrlIsOpen && (
                <NoOnlineTicketUrlModal
                    modalIsOpen={noTicketUrlIsOpen}
                    setModalIsOpen={setNoTicketUrlIsOpen}
                />
            )}
        </>
    );
}
